import { useState, useEffect } from 'react';
import { getCurrentTerms, agreeToTerms } from '../../services/termsApi/termsApi';
import { TermsOfService } from '../../services/termsApi/termsApi.types';
import { useSnackbar, SnackbarType } from '../../contexts/SnackbarProvider';

interface UseTermsAgreementModalProps {
  isOpen: boolean;
  onAgree: () => void;
}

export const useTermsAgreementModal = ({ isOpen, onAgree }: UseTermsAgreementModalProps) => {
  const [terms, setTerms] = useState<TermsOfService | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [agreeing, setAgreeing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const showSnackbar = useSnackbar();

  // Escキーでモーダルを閉じることを防ぐ
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  // 利用規約を取得
  useEffect(() => {
    if (isOpen) {
      loadTerms();
    }
  }, [isOpen]);

  const loadTerms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCurrentTerms();
      setTerms(response.terms);
    } catch (err) {
      console.error('Failed to load terms:', err);
      setError('利用規約の読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleAgree = async () => {
    if (!terms) return;

    try {
      setAgreeing(true);
      await agreeToTerms({ terms_id: terms.id });
      showSnackbar('利用規約に同意しました', SnackbarType.SUCCESS);
      onAgree();
    } catch (err) {
      console.error('Failed to agree to terms:', err);
      showSnackbar('同意の記録に失敗しました', SnackbarType.ERROR);
    } finally {
      setAgreeing(false);
    }
  };

  // オーバーレイクリックを無効化するハンドラー
  const handleOverlayClick = (event: React.MouseEvent) => {
    // オーバーレイ自体がクリックされた場合のみ処理
    if (event.target === event.currentTarget) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return {
    terms,
    loading,
    agreeing,
    error,
    handleAgree,
    handleOverlayClick,
  };
};